/* eslint-disable import/exports-last */
import {
  type CSSProperties,
  type ForwardedRef,
  type ReactNode,
  RefObject,
  type UIEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { noop } from '@betterplace/utils'
import { useForwardedRef, useResizeObserver } from '../../hooks'
import type { RowRange, VirtualScrollItemStyle, VirtualScrollProps } from './types'

export function setHeightsAndPositionsInitially(
  heights: number[],
  heightRefs: RefObject<Array<number>>,
  positionRefs: RefObject<Array<number>>,
  gap: number
) {
  const positions: number[] = [0]
  for (let i = 0; i < heights.length; i++) {
    positions[i + 1] = positions[i]! + heights[i]! + gap
  }

  heightRefs.current = heights
  positionRefs.current = positions
}

export function handleHeightsInitially(
  children: HTMLCollection,
  heightRefs: RefObject<Array<number>>,
  positionRefs: RefObject<Array<number>>,
  gap: number,
  setRowHeight: React.Dispatch<React.SetStateAction<number>>,
  rowHeight: number
) {
  if (heightRefs.current?.length) return

  const rowHeights = Array.from(children).map((child) => child.clientHeight)
  if (!rowHeights.length) return

  if (!rowHeight) {
    setRowHeight(rowHeights[0]!)
    return
  }

  setHeightsAndPositionsInitially(rowHeights, heightRefs, positionRefs, gap)
}

export function setHeights(
  heightRefs: RefObject<Array<number>>,
  rows: Map<number, HTMLDivElement>,
  medianHeight: number,
  positionRefs: RefObject<Array<number>>,
  gap: number
) {
  const initialHeightsLength = heightRefs.current?.length
  if (!rows.size || !initialHeightsLength) return

  for (const [key, value] of rows) {
    if (key >= initialHeightsLength) {
      const height = value?.clientHeight || medianHeight
      heightRefs.current[key] = height
      const prevPosition = positionRefs.current[key - 1] || key * (medianHeight + gap)
      const prevHeight = heightRefs.current[key - 1] || medianHeight
      positionRefs.current[key] = prevPosition + prevHeight + gap
    }
  }
}

function handleHeights(
  children: HTMLCollection | undefined,
  heightRefs: RefObject<Array<number>>,
  rows: Map<number, HTMLDivElement>,
  rowHeight: number,
  positionRefs: RefObject<Array<number>>,
  gap: number
) {
  if (!children?.length) return

  setHeights(heightRefs, rows, rowHeight, positionRefs, gap)
}

export function getMedianRowHeight(rowHeights: number[]) {
  if (!rowHeights.length) return 0

  const values = [...rowHeights].sort((a, b) => a - b)
  const half = Math.floor(values.length / 2)

  return values.length % 2 ? values[half] : (values[half - 1]! + values[half]!) / 2
}

export function createStyleProps(
  row: number,
  gap: number,
  medianRowHeight: number,
  cachedHeight: number | undefined,
  cachedPosition: number | undefined
): VirtualScrollItemStyle {
  const height = cachedHeight ?? medianRowHeight
  const position = cachedPosition ?? row * (medianRowHeight + gap)

  return {
    position: 'absolute',
    inset: 0,
    height: `${height}px`,
    transform: `translateY(${position}px)`,
  }
}

const ScrollingStates = {
  Up: 'Up',
  Down: 'Down',
  None: null,
} as const

type ScrollingStates = (typeof ScrollingStates)[keyof typeof ScrollingStates]

// eslint-disable-next-line import/exports-last
export function VirtualScrollSSRFriendly(props: VirtualScrollProps) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
    return () => {
      setMounted(false)
    }
  }, [])
  // do not render the if we're in SSR mode as useLayoutEffect is unavailable
  if (!mounted) return null

  return <VirtualScroll {...props} />
}

// eslint-disable-next-line import/exports-last
export function getScrollPosOnHeightChange(rowHeight: number, gap: number, row: number, scrollPos: number) {
  const totalRowHeight = rowHeight + gap
  const rowPos = row * totalRowHeight
  const positionInARow = scrollPos % totalRowHeight
  const newScrollPos = rowPos + positionInARow
  return newScrollPos
}

export function getRowIndex(
  estimatedRow: number,
  scrollPos: number,
  positions: number[],
  rowHeight: number,
  gap: number
): number {
  const estimatedRowPos = positions[estimatedRow]

  if (typeof estimatedRowPos !== 'number') return Math.floor(scrollPos / (rowHeight + gap))

  if (estimatedRowPos === scrollPos) return estimatedRow
  const length = positions.length

  if (estimatedRowPos > scrollPos) {
    for (let i = estimatedRow - 1; i >= 0; i--) {
      const pos = positions[i]
      if (typeof pos === 'number' && pos <= scrollPos) return i
    }
    return 0
  }

  if (estimatedRowPos < scrollPos) {
    for (let i = estimatedRow + 1; i < length; i++) {
      const pos = positions[i]
      if (typeof pos === 'number' && pos > scrollPos) return i - 1
    }
  }

  return Math.floor(scrollPos / (rowHeight + gap))
}

function propsHaveChanged(prevProps: Record<string, unknown> | undefined, props: Record<string, unknown> | undefined) {
  if (prevProps === props) return false
  if (!prevProps && props) return true
  if (!props && prevProps) return true
  return Object.keys(props!).some((key) => props![key] !== prevProps![key])
}
/**
 * Stabilizes the props of the first row to avoid unnecessary re-renders
 * Check if any of the props have changed and if so, update the state
 * @param props - The props to stabilize
 * @returns The stabilized props
 */
function useStabilizedProps(props: Record<string, unknown> | undefined) {
  const [prevProps, setPrevProps] = useState(props)
  useEffect(() => {
    if (propsHaveChanged(prevProps, props)) {
      setPrevProps(props)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props])
  return prevProps
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useThrottledCallback(callback: (...args: any[]) => void, delay: number) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastTimeCalledRef = useRef(0)
  return useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...args: any[]) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const call = () => requestAnimationFrame(() => callback(...args))

      const now = Date.now()
      if (now - lastTimeCalledRef.current < delay) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(call, delay)
      } else {
        call()
        lastTimeCalledRef.current = now
      }
    },
    [callback, delay]
  )
}

const VirtualScroll = forwardRef(function VirtualScroll(
  {
    className,
    style,
    children: Row,
    overscanRows: overscanRows_,
    maxRows: maxRows_,
    rowHeight: initialRowHeight = 0,
    visibleRowCount,
    onScroll: onScrollExternal,
    firstVisibleRowIndex,
    gap = 0,
    reactiveProps: reactiveProps_,
    onAfterScrollToIndex,
    suppressScroll = false,
  }: VirtualScrollProps,
  fwdRef: ForwardedRef<HTMLDivElement | null>
) {
  const stabilizedProps = useStabilizedProps(reactiveProps_)
  const [containerRef, ref] = useForwardedRef<HTMLDivElement | null>(fwdRef)
  const overscanRows = overscanRows_ || visibleRowCount // do not allow for a zero
  const styleRefs = useRef<Map<number, VirtualScrollItemStyle>>(new Map())
  const heightRefs = useRef<Array<number>>([])
  const positionRefs = useRef<Array<number>>([0])
  const [rowHeight, setRowHeight] = useState(initialRowHeight)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const heightChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const scrollPos = useRef<number>(((firstVisibleRowIndex ?? 0) + gap) * rowHeight)
  const [rowIndex, setRowIndex] = useState<number>(0)
  const rowRef = useRef<number>(0)
  const scrollingState = useRef<ScrollingStates>(ScrollingStates.None)
  const maxRows = maxRows_ ?? rowIndex + (visibleRowCount + overscanRows) * 2 + 1
  const containerHeight = visibleRowCount * (rowHeight + gap)
  const totalHeight = (rowHeight + gap) * maxRows
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const firstChangedRowRef = useRef<number | null>(null)
  const [, forceRender] = useState(0)

  const getRefCallback = useCallback((i: number) => {
    return (el: HTMLDivElement | null) => {
      if (el) {
        rowRefs.current.set(i, el)
      }
    }
  }, [])

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { width } = useResizeObserver({ ref: containerRef })
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => {
    const children = containerRef.current?.firstElementChild?.children
    if (!children?.length) return

    if (heightRefs.current?.length && rowHeight === initialRowHeight) {
      const rowHeights = Array.from(children).map((child) => child.clientHeight)

      const medianRowHeight = getMedianRowHeight(rowHeights)
      if (!medianRowHeight) return
      setRowHeight(medianRowHeight)

      return
    }

    handleHeightsInitially(children, heightRefs, positionRefs, gap, setRowHeight, rowHeight)
  }, [containerRef, width, visibleRowCount, maxRows, initialRowHeight, gap, rowHeight, rowIndex])

  // TODO DEV-26456 move logic into functions and add tests
  const batchUpdateHeightsAndPositions = useCallback(() => {
    const index = firstChangedRowRef.current
    firstChangedRowRef.current = null
    if (!heightRefs.current?.length || !(typeof index === 'number')) return

    for (let i = index + 1; i < heightRefs.current.length + 1; i++) {
      positionRefs.current[i] = positionRefs.current[i - 1]! + heightRefs.current[i - 1]! + gap
    }

    for (let key = index; key <= heightRefs.current.length; key++) {
      const height = heightRefs.current[key]
      const position = positionRefs.current[key]
      const prevStyle = styleRefs.current.get(key)

      const newStyle = createStyleProps(key, gap, rowHeight, height, position)

      if (!prevStyle || prevStyle.height !== newStyle.height || prevStyle.transform !== newStyle.transform) {
        styleRefs.current.set(key, newStyle)
      }
    }

    forceRender((prev) => prev + 1)
  }, [gap, rowHeight])

  const onSizeHasChangedDebounced = useCallback(() => {
    if (heightChangeTimeoutRef.current !== null) clearTimeout(heightChangeTimeoutRef.current)
    heightChangeTimeoutRef.current = setTimeout(() => {
      batchUpdateHeightsAndPositions?.()
      heightChangeTimeoutRef.current = null
    }, 10)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSizeHasChanged = useCallback(
    (height: number, i: number) => {
      if (!heightRefs.current?.length) return

      const oldHeight = heightRefs.current[i] ?? 0
      if (height === oldHeight) return

      heightRefs.current[i] = height
      if (typeof firstChangedRowRef.current === 'number' && firstChangedRowRef.current <= i) return

      firstChangedRowRef.current = i
      onSizeHasChangedDebounced()
    },
    [onSizeHasChangedDebounced]
  )

  function getStyle(index: number): CSSProperties {
    const cachedStyle = styleRefs.current.get(index)
    if (cachedStyle) return cachedStyle as CSSProperties

    const cachedHeight = heightRefs.current[index]
    const cachedPosition = positionRefs.current[index]
    const styleProps = createStyleProps(index, gap, rowHeight, cachedHeight, cachedPosition)
    if (typeof cachedHeight === 'number' && typeof cachedPosition === 'number') styleRefs.current.set(index, styleProps)

    return styleProps as CSSProperties
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getRange = useCallback(
    (row: number): RowRange => {
      const startIndex = Math.max(0, row - overscanRows)
      const endIndex = Math.min(
        maxRows - 1, // don't render past the end of the list
        row + visibleRowCount + overscanRows
      )
      return [startIndex, endIndex, row]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxRows, overscanRows, visibleRowCount]
  )
  const items: ReactNode[] = []

  const [start, end] = getRange(rowIndex)

  for (let i = start; i <= end; i++) {
    const refCallback = getRefCallback(i)
    const handleSizeChange = (height: number) => onSizeHasChanged(height, i)

    items.push(<Row index={i} style={getStyle(i)} key={i} ref={refCallback} onChangeSize={handleSizeChange} />)
  }

  const onScrollDebounced = useCallback(
    (range: RowRange) => {
      if (scrollTimeoutRef.current !== null) clearTimeout(scrollTimeoutRef.current)
      scrollTimeoutRef.current = setTimeout(() => {
        onScrollExternal?.(range)
        scrollTimeoutRef.current = null
      }, 50)
    },
    [onScrollExternal]
  )

  useEffect(() => {
    rowRef.current = rowIndex
  }, [rowIndex])

  useLayoutEffect(() => {
    const currentScrollPos = containerRef.current?.scrollTop ?? 0
    const scrollPos = getScrollPosOnHeightChange(rowHeight, gap, rowIndex, currentScrollPos)
    containerRef.current?.scrollTo?.({ left: 0, top: scrollPos, behavior: 'instant' })
    onScrollExternal?.(getRange(rowIndex))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowHeight, gap, maxRows])

  const onScroll: UIEventHandler<HTMLDivElement> = useCallback(
    (ev) => {
      const children = containerRef.current?.firstElementChild?.children
      handleHeights(children, heightRefs, rowRefs.current, rowHeight, positionRefs, gap)

      const prevScrollPos = scrollPos.current
      if (!suppressScroll) {
        // ev.currentTarget is null. using target instead which is working, even though according to eslint it should not have scrollTop
        scrollPos.current = (ev.target as HTMLDivElement).scrollTop
      }
      // with suppressScroll we ignore scroll events that are triggered outside of the
      // component before we can reply on firstVisibleRowIndex to set the correct position
      const row = getRowIndex(
        rowIndex,
        suppressScroll ? prevScrollPos : scrollPos.current,
        positionRefs.current,
        rowHeight,
        gap
      )
      scrollingState.current = prevScrollPos < scrollPos.current ? ScrollingStates.Down : ScrollingStates.Up
      onScrollDebounced(getRange(row))
      setRowIndex(row)
    },
    [rowIndex, onScrollDebounced, getRange, containerRef, rowHeight, gap, suppressScroll]
  )

  const throttledOnScroll: UIEventHandler<HTMLDivElement> = useThrottledCallback(onScroll, 30)

  useEffect(() => {
    if (typeof firstVisibleRowIndex !== 'number') return
    if (firstVisibleRowIndex <= rowIndex) {
      if (onAfterScrollToIndex) onAfterScrollToIndex()
      return
    }

    const position = positionRefs.current[rowIndex]
      ? positionRefs.current[rowIndex] + (firstVisibleRowIndex - rowIndex) * (rowHeight + gap)
      : firstVisibleRowIndex * (rowHeight + gap)
    containerRef.current?.scrollTo?.({ left: 0, top: position, behavior: 'instant' })
    onScrollExternal?.(getRange(firstVisibleRowIndex))
    setRowIndex(firstVisibleRowIndex)
    if (onAfterScrollToIndex) onAfterScrollToIndex()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstVisibleRowIndex])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef?.current) clearTimeout(scrollTimeoutRef.current)
      if (heightChangeTimeoutRef?.current) clearTimeout(heightChangeTimeoutRef.current)
    }
  }, [])

  return (
    <div
      className={className}
      style={{
        // allow to override the height
        height: containerHeight,
        ...style,
        overflowY: 'auto',
        position: 'relative',
        scrollBehavior: 'smooth',
        willChange: 'contents',
        maxHeight: containerHeight,
      }}
      ref={ref}
      onScroll={throttledOnScroll}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: totalHeight }}>
        <Row {...stabilizedProps} index={-1} key={-1} style={{ opacity: '0', pointerEvents: 'none' }} ref={noop} />
        {items}
      </div>
    </div>
  )
})

export default VirtualScroll
